import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import ReviewsSummary from '../../../../../components/ReviewsSummary/ReviewsSummary';
import { Flex } from '../../../../../flayr-components/Flex/Flex';
import { Heading } from '../../../../../flayr-components/Heading/Heading';
import { Img } from '../../../../../flayr-components/Img/Img';
import { Modal } from '../../../../../flayr-components/Modal/Modal';
import { Review } from '../../../../../flayr-components/Review/Review';
import { ShowIf } from '../../../../../flayr-components/ShowIf/ShowIf';
import { StarRating } from '../../../../../flayr-components/StarRating/StarRating';
import useClearableState from '../../../../../hooks/useClearableState';
import useDeviceMediaQuery from '../../../../../hooks/useDeviceMediaQuery';
import Pagination from '../../../../../lib/Pagination/Pagination';
import Portal from '../../../../../lib/Portal/Portal';
import { IReview } from '../../../../../types/review';
import { IReviewDimension } from '../../../../../types/reviewFormData';
import ProviderProfileCollapseContainer from '../../../ProviderProfilePage/ProviderProfileCollapseContainer/ProviderProfileCollapseContainer';

interface IReviewsSectionProps {
  reviewItems: IReview[];
  summary: IReviewDimension[];
  totalCount: number;
  averageScore: number;
  name?: string;
  pagination: {
    page: number;
    limit: number;
  };
  onNext?: (e?: any) => void;
}

const ReviewsSection: React.FC<React.PropsWithChildren<IReviewsSectionProps>> = ({
  totalCount,
  averageScore,
  reviewItems = [],
  summary = [],
  name,
  pagination,
  onNext,
}) => {
  const [previewImg, setPreviewImg, clearPreviewImg] = useClearableState('');
  const { isDesktop } = useDeviceMediaQuery();

  function handleImgSelect(e) {
    setPreviewImg(e.src);
  }

  return (
    <section className="w-full">
      <ProviderProfileCollapseContainer
        open={isDesktop}
        hideIcon={isDesktop}
        disabled={isDesktop}
        title={
          <Flex>
            <Heading className="text-lg text-black leading-[1.5em] lg:text-4xl">
              Reviews
            </Heading>
            <ShowIf condition={averageScore}>
              <div className="flex items-end pl-3 pr-4 lg:items-center lg:ml-0">
                <span className="text-sm leading-[1.5em] font-normal text-gray-89 lg:text-black lg:text-2xl">
                  ({' '}
                  <span className="pt-1 font-semibold text-gray-700 ">
                    {averageScore}
                  </span>
                  <span className="mx-1 mr-3 text-review-star lg:text-2xl lg:mx-4">
                    <FontAwesomeIcon icon={faStar} />
                  </span>
                  {totalCount} <span className="">reviews</span> )
                </span>
              </div>
            </ShowIf>
          </Flex>
        }
      >
        <ReviewsSummary
          items={
            (summary?.map((d) => ({
              label: d.label,
              rating: d.score,
            })) as any) || []
          }
          providerName={name}
          className="mb-5 text-sm"
        />
        <ShowIf
          condition={reviewItems.length}
          elseTemplate={
            <div className="px-4 py-2 -mx-4 -mt-2 -mb-2 text-sm leading-tight text-gray-600 bg-gray-100">
              This is a new provider. <br /> There's no review to show yet.
            </div>
          }
        >
          {reviewItems.map((rev, key) => {
            const {
              publicFeedback,
              reviewScore,
              customerName,
              customerReviewDate,
              media,
            } = rev;
            return (
              <div className="mt-10 text-sm lg:text-lg" key={key}>
                <Review
                  name={customerName || ''}
                  date={customerReviewDate || ''}
                  comments={publicFeedback}
                  rating={reviewScore}
                  media={media}
                  mediaItemWidthClassName="w-5/12 lg:w-44"
                  onMediaClick={handleImgSelect}
                />
              </div>
            );
          })}
        </ShowIf>
        <div
          hidden={totalCount <= reviewItems.length}
          className="w-2/3 mx-auto mt-6 mb-4"
        >
          <Pagination
            total={totalCount}
            page={+(pagination?.page || 0)}
            limit={+(pagination?.limit || 1)}
            currentRecordsCount={reviewItems?.length}
            className="mt-12"
            type="loadMore"
            loadMoreLabel="Show More"
            loadMoreBtnProps={{
              color: 'hot-pink',
              round: true,
              outline: true,
              block: false,
              size: (isDesktop && 'md') || 'sm',
            }}
            onLoadMore={onNext}
          />
        </div>
      </ProviderProfileCollapseContainer>
      <Portal>
        <Modal
          open={!!previewImg}
          size="sm"
          footer
          cancelBtnProps={{ hidden: true }}
          okLabel="Close"
          onClose={clearPreviewImg}
          onOk={clearPreviewImg}
          removeWhenClosed
        >
          <Img src={previewImg} />
        </Modal>
      </Portal>
    </section>
  );
};
export default ReviewsSection;
